/**
 * BookSummaryShowcase — Premium renderer for book summary / review layouts.
 * Palette: amber primary + violet concepts + teal quotes.
 * Phase 8 — Athena Presentation Design System.
 */
import React, { useState } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, SectionCard, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  accent:        '#f59e0b',
  accentFade:    'rgba(245,158,11,0.10)',
  accentBorder:  'rgba(245,158,11,0.22)',
  violet:        '#a78bfa',
  violetFade:    'rgba(167,139,250,0.10)',
  violetBorder:  'rgba(167,139,250,0.22)',
  teal:          '#2dd4bf',
  tealFade:      'rgba(45,212,191,0.10)',
  tealBorder:    'rgba(45,212,191,0.22)',
  green:         '#34d399',
  greenFade:     'rgba(52,211,153,0.10)',
  greenBorder:   'rgba(52,211,153,0.22)',
  card:          base.card,
  border:        base.border,
  text:          base.text,
  textMuted:     base.textMuted,
  textFaint:     base.textFaint,
};

const _card = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 14,
  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
};

function HeroDesktop({ d }) {
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS = [
    { icon: '✍️', value: d.author || 'Autor',           label: 'Autor'   },
    { icon: '🏷',  value: d.genre  || 'Literatura',      label: 'Gênero'  },
    { icon: '⭐', value: d.rating  || 'Recomendado',     label: 'Avaliação' },
    d.year && { icon: '📅', value: d.year,               label: 'Ano'     },
  ].filter(Boolean);

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="📚 RESUMO DE LIVRO"
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
        placeholder="📚" placeholderLabel="Livro"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 6px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.author && <div style={{ fontSize: 14, color: C.accent, fontWeight: 600, marginBottom: 6 }}>por {d.author}</div>}
          {d.subtitle && !d.author && (
            <p style={{ margin: '0 0 8px', fontSize: 13, color: base.textSec, lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{d.subtitle}</p>
          )}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.accent} />
      </div>
    </div>
  );
}

function HeroMobile({ d }) {
  const METRICS = [
    { icon: '✍️', value: d.author  || 'Autor',       label: 'Autor'    },
    { icon: '🏷',  value: d.genre   || 'Literatura',  label: 'Gênero'   },
    { icon: '⭐', value: d.rating   || 'Recomendado', label: 'Avaliação' },
    { icon: '📅', value: d.year     || 'Atual',       label: 'Ano'      },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>📚 RESUMO</span>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.author && <div style={{ fontSize: 13, color: C.accent, fontWeight: 600, marginTop: 5 }}>por {d.author}</div>}
          {d.subtitle && !d.author && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{d.subtitle}</p>
          )}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
          placeholder="📚"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

function ConceptCard({ concept, index }) {
  return (
    <div style={{
      ..._card, padding: '12px 14px', borderLeft: `3px solid ${C.violet}`,
      display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: '50%', background: C.violetFade,
        border: `1px solid ${C.violetBorder}`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 10, fontWeight: 800, color: C.violet,
        flexShrink: 0, marginTop: 1,
      }}>{index + 1}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: concept.description ? 4 : 0 }}>{concept.title}</div>
        {concept.description && (
          <p style={{ margin: 0, fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>{concept.description}</p>
        )}
      </div>
    </div>
  );
}

function ChapterRow({ chapter, compact }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', color: C.text }}
      >
        {chapter.number != null && (
          <span style={{ fontSize: 10, fontWeight: 800, color: C.accent, background: 'rgba(245,158,11,0.15)', border: `1px solid ${C.accentBorder}`, borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>
            {typeof chapter.number === 'number' ? `Cap. ${chapter.number}` : chapter.number}
          </span>
        )}
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1 }}>{chapter.title}</span>
        {chapter.summary && (
          <span style={{ fontSize: 11, color: C.textFaint }}>{open ? '▲' : '▼'}</span>
        )}
      </button>
      {open && chapter.summary && (
        <div style={{ padding: '0 12px 10px', fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>
          {chapter.summary}
        </div>
      )}
    </div>
  );
}

function QuoteCard({ quote, index }) {
  return (
    <div style={{
      background: C.tealFade, border: `1px solid ${C.tealBorder}`,
      borderLeft: `3px solid ${C.teal}`, borderRadius: 8, padding: '12px 14px', marginBottom: 8,
    }}>
      <span style={{ fontSize: 20, color: C.teal, fontWeight: 800, lineHeight: 1, display: 'block', marginBottom: 4 }}>"</span>
      <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.6, fontStyle: 'italic' }}>{quote}</p>
    </div>
  );
}

export default function BookSummaryShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  const keyConcepts = d.key_concepts || [];
  const chapters    = d.chapters     || [];
  const quotes      = d.quotes       || [];
  const lessons     = d.lessons      || [];

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <HeroMobile d={d} /> : <HeroDesktop d={d} />}

      {(d.synopsis || d.context) && (
        <SectionCard title="Sobre o Livro" borderColor={C.accentBorder} style={{ marginBottom: 12 }}>
          <ProseText
            text={d.synopsis || d.context}
            style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
          />
        </SectionCard>
      )}

      {keyConcepts.length > 0 && (
        <SectionCard title="Conceitos-chave" borderColor={C.violetBorder} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {keyConcepts.map((c, i) => <ConceptCard key={i} concept={c} index={i} />)}
          </div>
        </SectionCard>
      )}

      {chapters.length > 0 && (
        <SectionCard title={`Capítulos (${chapters.length})`} borderColor={C.accentBorder} style={{ marginBottom: 12 }}>
          {chapters.map((ch, i) => <ChapterRow key={i} chapter={ch} compact={isMobile} />)}
        </SectionCard>
      )}

      {lessons.length > 0 && (
        <SectionCard title="Lições & Aprendizados" borderColor={C.greenBorder} style={{ marginBottom: 12 }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lessons.map((lesson, i) => (
              <li key={i} style={{ display: 'flex', gap: 8, fontSize: 14, color: C.text }}>
                <span style={{ color: C.green, flexShrink: 0, marginTop: 3 }}>✓</span>
                <span style={{ lineHeight: 1.5 }}>{lesson}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {quotes.length > 0 && (
        <SectionCard title="Citações" borderColor={C.tealBorder} style={{ marginBottom: 12 }}>
          {quotes.map((q, i) => <QuoteCard key={i} quote={q} index={i} />)}
        </SectionCard>
      )}

      {(d.sections || []).map((s, i) => (
        <SectionCard key={i} title={s.title} borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.accent} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </div>
  );
}
